import { useState, useEffect } from 'react';
import { FileText, Settings, ChevronRight, Shield, Globe, X, RotateCcw, Wand2, Check, Save } from 'lucide-react';
import { showToast } from '@/lib/toast';
import { DocumentSettings } from '@/types';

interface DocumentSettingsPanelProps {
  settings: DocumentSettings;
  onUpdateSettings: (key: keyof DocumentSettings, value: any) => void;
  onClose?: () => void;
}

export default function DocumentSettingsPanel({ 
  settings, 
  onUpdateSettings,
  onClose 
}: DocumentSettingsPanelProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  // Estados para botão Salvar e banner
  const [savedSettings, setSavedSettings] = useState<DocumentSettings | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const isDarkMode = document.documentElement.classList.contains('dark');

  const documentFormats = [
    { value: 'pdf', label: 'PDF', description: 'Portable Document Format' },
    { value: 'docx', label: 'DOCX', description: 'Microsoft Word' },
    { value: 'doc', label: 'DOC', description: 'Microsoft Word Legacy' },
    { value: 'odt', label: 'ODT', description: 'OpenDocument Text' },
    { value: 'rtf', label: 'RTF', description: 'Rich Text Format' },
    { value: 'txt', label: 'TXT', description: 'Texto Simples' }
  ];

  const qualityOptions = [
    { value: 'low', label: 'Baixa', description: 'Menor tamanho, qualidade reduzida' },
    { value: 'medium', label: 'Média', description: 'Equilibrio entre tamanho e qualidade' },
    { value: 'high', label: 'Alta', description: 'Boa qualidade, tamanho moderado' },
    { value: 'maximum', label: 'Máxima', description: 'Melhor qualidade, maior tamanho' }
  ];

  const documentPresets = [
    {
      name: 'Arquivo Web',
      format: 'pdf',
      quality: 'medium',
      preserveMetadata: false,
      optimizeForWeb: true,
      description: 'Otimizado para compartilhamento online'
    },
    {
      name: 'Documento Profissional',
      format: 'pdf',
      quality: 'high',
      preserveMetadata: true,
      optimizeForWeb: false,
      description: 'Para impressão e arquivamento'
    },
    {
      name: 'Word Compatível',
      format: 'docx',
      quality: 'high',
      preserveMetadata: true,
      optimizeForWeb: false,
      description: 'Para edição no Microsoft Word'
    },
    {
      name: 'Texto Simples',
      format: 'txt',
      quality: 'medium',
      preserveMetadata: false,
      optimizeForWeb: true,
      description: 'Apenas texto, sem formatação'
    }
  ];

  const applyPreset = (preset: typeof documentPresets[0]) => {
    onUpdateSettings('format', preset.format);
    onUpdateSettings('quality', preset.quality);
    onUpdateSettings('preserveMetadata', preset.preserveMetadata);
    onUpdateSettings('optimizeForWeb', preset.optimizeForWeb);
    setSelectedPreset(preset.name);
  };

  const resetToDefaults = () => {
    onUpdateSettings('format', 'pdf');
    onUpdateSettings('quality', 'medium');
    onUpdateSettings('preserveMetadata', true);
    onUpdateSettings('optimizeForWeb', false);
    setSelectedPreset(null);
  };

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'pdf':
        return '📄';
      case 'docx':
      case 'doc':
        return '📝';
      case 'odt':
        return '📋';
      case 'rtf':
        return '📄';
      case 'txt':
        return '📃';
      default:
        return '📄';
    }
  };

  /* ========= Persistência ========= */
  useEffect(() => {
    const saved = localStorage.getItem('converterPro_documentSettings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSavedSettings(parsed);
      } catch (e) {
        console.error('Erro ao carregar configurações salvas:', e);
      }
    } else {
      setSavedSettings({ ...settings });
    }
  }, []);

  // Detectar mudanças
  useEffect(() => {
    if (savedSettings) {
      const changed = Object.keys(settings).some(key => {
        const current = settings[key as keyof DocumentSettings];
        const savedVal = savedSettings[key as keyof DocumentSettings];
        return JSON.stringify(current) !== JSON.stringify(savedVal);
      });
      setHasUnsavedChanges(changed);
      // no banner
    }
  }, [settings, savedSettings]);

  const handleSaveSettings = () => {
    try {
      localStorage.setItem('converterPro_documentSettings', JSON.stringify(settings));
      setSavedSettings({ ...settings });
      setHasUnsavedChanges(false);
      showToast('✅ Configurações salvas e aplicadas');
      if (onClose) onClose();
    } catch (e) {
      console.error('Erro ao salvar configurações:', e);
    }
  };

  return (
    <div className={`${
      isDarkMode ? 'bg-[#383D52] border-[#4A4F64]' : 'bg-white border-[#E5E5E5]'
    } border rounded-2xl overflow-hidden shadow-xl w-full`}>
      {/* Header */}
      <div className={`p-3 sm:p-4 border-b ${
        isDarkMode ? 'border-[#4A4F64]' : 'border-[#E5E5E5]'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <FileText className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
            </div>
            <div>
              <h3 className={`text-base sm:text-lg font-bold ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                <span className="hidden sm:inline">Configurações de Documento</span>
                <span className="sm:hidden">Configurações</span>
              </h3>
              <p className={`text-xs ${
                isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
              }`}>
                <span className="hidden sm:inline">Personalize suas conversões de documento</span>
                <span className="sm:hidden">Personalize conversões</span>
              </p>
            </div>
          </div>
          
          {/* Botões e banner */}
          <div className="flex flex-col items-end space-y-1 sm:space-y-1.5">
            <div className="flex items-center space-x-1 sm:space-x-2">
              {/* Botão Salvar */}
              <button
                onClick={handleSaveSettings}
                disabled={!hasUnsavedChanges}
                className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 ${
                  hasUnsavedChanges
                    ? 'bg-green-500 hover:bg-green-600 text-white shadow-md hover:shadow-lg'
                    : isDarkMode
                      ? 'bg-[#4A4F64] text-[#EAECF3]/50 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                title={hasUnsavedChanges ? 'Salvar configurações' : 'Nenhuma alteração para salvar'}
              >
                <Save className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Salvar</span>
                {hasUnsavedChanges && (
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                )}
              </button>

              {/* Botão Reset */}
              <button
                onClick={resetToDefaults}
                className={`p-1.5 sm:p-2 rounded-lg ${
                  isDarkMode ? 'bg-[#4A4F64] hover:bg-[#52576B] text-[#EAECF3]' : 'bg-[#F0F0F0] hover:bg-[#E5E5E5] text-[#4A4A69]'
                } transition-colors`}
                title="Restaurar padrões"
              >
                <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>

              {onClose && (
                <button
                  onClick={onClose}
                  className={`p-1.5 sm:p-2 rounded-lg ${
                    isDarkMode ? 'hover:bg-red-500/20 text-red-300' : 'hover:bg-red-50 text-red-600'
                  } transition-colors`}
                >
                  <X className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 max-h-[70vh] overflow-y-auto">
        {/* Presets Rápidos */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <Wand2 className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-500" />
            <h4 className={`font-semibold text-xs sm:text-sm ${
              isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
            }`}>
              Presets Rápidos
            </h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {documentPresets.map((preset, index) => {
              const isSelected = selectedPreset === preset.name;
              return (
              <button
                key={preset.name}
                onClick={() => applyPreset(preset)}
                className={`group relative p-2 sm:p-3 rounded-lg border transition-all duration-500 hover:scale-105 hover:shadow-lg text-left ${
                                      isSelected
                      ? 'bg-blue-500 border-blue-400 text-white shadow-lg shadow-blue-500/25'
                    : isDarkMode 
                    ? 'bg-[#2A2E3F] border-[#4A4F64] hover:bg-[#383D52] text-[#EAECF3] hover:border-purple-400/50' 
                    : 'bg-[#F8F9FA] border-[#E5E5E5] hover:bg-white text-[#4A4A69] hover:border-purple-400/50'
                }`}
                style={{ 
                  animationDelay: `${index * 150}ms`
                }}
              >
                {/* Ícone de check quando ativo */}
                {isSelected && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full flex items-center justify-center z-10">
                    <Check className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-purple-500" />
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <span className={`text-base sm:text-lg transition-all duration-300 ${
                      isSelected 
                        ? 'filter brightness-0 invert' 
                        : 'group-hover:scale-110'
                    }`}>{getFormatIcon(preset.format)}</span>
                    <div>
                      <div className={`font-medium text-xs sm:text-sm transition-colors duration-300 ${
                        isSelected ? 'text-white' : 'text-current'
                      }`}>
                        {preset.name}
                      </div>
                      <div className={`text-xs transition-colors duration-300 ${
                        isSelected ? 'text-purple-100' : isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                      }`}>
                        <span className="hidden sm:inline">{preset.description}</span>
                        <span className="sm:hidden">{preset.format.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                  <ChevronRight className={`w-3 h-3 sm:w-4 sm:h-4 transition-all duration-300 ${
                    isSelected 
                      ? 'text-white' 
                      : 'group-hover:translate-x-1 ' + (isDarkMode ? 'text-[#EAECF3]/50' : 'text-[#4A4A69]/50')
                  }`} />
                </div>
                
                {/* Efeito de brilho quando selecionado - agora permanente */}
                {isSelected && (
                  <>
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-purple-400 to-indigo-400 opacity-20" />
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-purple-300 to-indigo-300 opacity-5" />
                  </>
                )}
                
                {/* Efeito de hover */}
                <div className={`absolute inset-0 rounded-lg bg-gradient-to-r from-purple-500/10 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                  isSelected ? 'hidden' : ''
                }`} />
              </button>
              );
            })}
          </div>
        </div>

        {/* Configurações Básicas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div>
            <label className={`block text-xs sm:text-sm font-medium mb-2 ${
              isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
            }`}>
              Formato de Saída
            </label>
            <select
              value={settings.format}
              onChange={(e) => onUpdateSettings('format', e.target.value)}
              className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border ${
                isDarkMode 
                  ? 'bg-[#2A2E3F] border-[#4A4F64] text-[#EAECF3]' 
                  : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
              }`}
            >
              {documentFormats.map(format => (
                <option key={format.value} value={format.value}>
                  <span className="hidden sm:inline">{format.label} - {format.description}</span>
                  <span className="sm:hidden">{format.label}</span>
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-xs sm:text-sm font-medium mb-2 ${
              isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
            }`}>
              Qualidade
            </label>
            <select
              value={settings.quality}
              onChange={(e) => onUpdateSettings('quality', e.target.value)}
              className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border ${
                isDarkMode 
                  ? 'bg-[#2A2E3F] border-[#4A4F64] text-[#EAECF3]' 
                  : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
              }`}
            >
              {qualityOptions.map(quality => (
                <option key={quality.value} value={quality.value}>
                  <span className="hidden sm:inline">{quality.label} - {quality.description}</span>
                  <span className="sm:hidden">{quality.label}</span>
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Configurações Avançadas */}
        <div className="mb-4">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`flex items-center justify-between w-full p-2 sm:p-3 rounded-lg transition-colors ${
              isDarkMode ? 'hover:bg-[#2A2E3F] border border-[#4A4F64]' : 'hover:bg-[#F8F9FA] border border-[#E5E5E5]'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Settings className={`w-3 h-3 sm:w-4 sm:h-4 ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`} />
              <span className={`text-xs sm:text-sm font-medium ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                <span className="hidden sm:inline">Configurações Avançadas</span>
                <span className="sm:hidden">Avançadas</span>
              </span>
            </div>
            <ChevronRight className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform ${
              showAdvanced ? 'rotate-90' : ''
            } ${isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'}`} />
          </button>

          {showAdvanced && (
            <div className="mt-3 sm:mt-4 space-y-3 sm:space-y-4 p-3 sm:p-4 rounded-lg border border-dashed border-purple-300/30">
              {/* Preservar Metadados */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                  <Shield className={`w-3 h-3 sm:w-4 sm:h-4 ${
                    isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs sm:text-sm font-medium ${
                      isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                    }`}>
                      Preservar Metadados
                    </div>
                    <div className={`text-xs ${
                      isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                    }`}>
                      <span className="hidden sm:inline">Manter informações do autor, data, etc.</span>
                      <span className="sm:hidden">Manter metadados</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => onUpdateSettings('preserveMetadata', !settings.preserveMetadata)}
                  className={`relative w-10 h-5 sm:w-12 sm:h-6 rounded-full transition-colors ${
                    settings.preserveMetadata ? 'bg-blue-500' : isDarkMode ? 'bg-[#4A4F64]' : 'bg-[#E5E5E5]'
                  }`}
                >
                  <div className={`absolute w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-full top-0.5 transition-transform ${
                    settings.preserveMetadata ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Otimizar para Web */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                  <Globe className={`w-3 h-3 sm:w-4 sm:h-4 ${
                    isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs sm:text-sm font-medium ${
                      isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                    }`}>
                      Otimizar para Web
                    </div>
                    <div className={`text-xs ${
                      isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                    }`}>
                      <span className="hidden sm:inline">Reduzir tamanho para compartilhamento</span>
                      <span className="sm:hidden">Reduzir tamanho</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => onUpdateSettings('optimizeForWeb', !settings.optimizeForWeb)}
                  className={`relative w-10 h-5 sm:w-12 sm:h-6 rounded-full transition-colors ${
                    settings.optimizeForWeb ? 'bg-green-500' : isDarkMode ? 'bg-[#4A4F64]' : 'bg-[#E5E5E5]'
                  }`}
                >
                  <div className={`absolute w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-full top-0.5 transition-transform ${
                    settings.optimizeForWeb ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Resumo das Configurações */}
        <div className={`p-3 sm:p-4 rounded-lg ${
          isDarkMode ? 'bg-[#2A2E3F] border border-[#4A4F64]' : 'bg-[#F8F9FA] border border-[#E5E5E5]'
        }`}>
          <h4 className={`text-xs sm:text-sm font-medium mb-2 ${
            isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
          }`}>
            Configuração Atual
          </h4>
          <div className={`text-xs sm:text-sm space-y-1 ${
            isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
          }`}>
            <div className="flex justify-between">
              <span>Formato:</span>
              <span className="font-medium">{settings.format.toUpperCase()}</span>
            </div>
            <div className="flex justify-between">
              <span>Qualidade:</span>
              <span className="font-medium">{qualityOptions.find(q => q.value === settings.quality)?.label}</span>
            </div>
            <div className="flex justify-between">
              <span>Metadados:</span>
              <span className="font-medium">{settings.preserveMetadata ? 'Preservar' : 'Remover'}</span>
            </div>
            <div className="flex justify-between">
              <span>Web:</span>
              <span className="font-medium">{settings.optimizeForWeb ? 'Otimizado' : 'Padrão'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 